// Console D's small controls: buttons (ink, ghost, small), the segmented control, underline tabs
// with mono counts, pills for values, tags (SOON, ENT), keys, the mono link tag and the empty
// state. Links styled as these use the class helpers (btnClass) on the router's <Link>.
import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Icon } from '../icons'

export type BtnVariant = 'solid' | 'ghost' | undefined

/** The class of a button, for a <button> or a link that looks like one. */
export function btnClass(variant?: BtnVariant, size?: 'sm' | undefined): string {
  return ['btn', variant ?? '', size ?? ''].filter(Boolean).join(' ')
}

export function Btn({
  variant,
  size,
  className,
  type = 'button',
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: BtnVariant
  size?: 'sm' | undefined
}) {
  return (
    <button
      type={type}
      className={[btnClass(variant, size), className ?? ''].filter(Boolean).join(' ')}
      {...rest}
    />
  )
}

export interface SegOption<T extends string> {
  value: T
  label: ReactNode
}

/** A segmented control: one of a few values, each a pressed or unpressed button. */
export function Seg<T extends string>({
  label,
  options,
  value,
  onChange,
  disabled,
}: {
  label: string
  options: readonly SegOption<T>[]
  value: T
  onChange: (value: T) => void
  /** Shown, not changeable: a member's view, or a locked value. */
  disabled?: boolean
}) {
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not a form fieldset
    <div className="seg" role="group" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          aria-pressed={o.value === value}
          disabled={disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

/**
 * Underline tabs. They are driven by the URL: each child is a link (the router's <Link>) with
 * aria-current="page" on the current one, so a tab is a place you can share and go back to.
 */
export function Tabs({ label, children }: { label: string; children: ReactNode }) {
  return (
    <nav className="tabs" aria-label={label}>
      {children}
    </nav>
  )
}

/** A tab's count, mono and quiet. */
export function TabCount({ n }: { n: ReactNode }) {
  return <span className="n">{n}</span>
}

/** A value with a tint: `500`, `1.24 s`. Never a status (Status). */
export function Pill({ tone, children }: { tone?: 'crash' | 'warn' | 'ok'; children: ReactNode }) {
  return <span className={tone ? `pill ${tone}` : 'pill'}>{children}</span>
}

/** SOON, ENT, EXAMPLE DATA: the site's tag. */
export function Tag({ children, title }: { children: ReactNode; title?: string }) {
  return (
    <span className="tag" title={title}>
      {children}
    </span>
  )
}

export function Kbd({ children }: { children: ReactNode }) {
  return <kbd translate="no">{children}</kbd>
}

/** The class of the small mono link tag (`.lk`), for the router's <Link>. */
export const LK = 'lk'

/** The small mono link tag, for a plain URL. */
export function Lk({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a className={LK} href={href}>
      {children}
    </a>
  )
}

/** Nothing to show, said calmly. `dashed` draws the outline of a place something will go. */
export function EmptyNote({
  title,
  children,
  dashed,
  tight,
}: {
  title: ReactNode
  children?: ReactNode
  dashed?: boolean
  /** Something of its own follows right under it (a project's deletion row): no bottom padding. */
  tight?: boolean | undefined
}) {
  return (
    <div
      className={['empty', dashed ? 'dashed' : '', tight ? 'tight' : ''].filter(Boolean).join(' ')}
    >
      <b>{title}</b>
      {children ? <span>{children}</span> : null}
    </div>
  )
}

/**
 * A native select dressed as a query chip (28 px), for a section head or a tool row: keyboard and
 * screen readers get the real control. `after` is the chip's own button (a step's ×); a chip
 * without one shows the chevron.
 */
export function SelectChip({
  prefix,
  label,
  value,
  onChange,
  children,
  translate,
  after,
}: {
  prefix?: ReactNode
  label: string
  value: string
  onChange: (value: string) => void
  children: ReactNode
  translate?: 'no'
  after?: ReactNode
}) {
  return (
    <span className="chip sel">
      {prefix ? <span>{prefix}</span> : null}
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        translate={translate}
      >
        {children}
      </select>
      {after ?? <Icon name="chev" small />}
    </span>
  )
}
